import { compact, isEmpty, maxBy } from 'lodash/fp';

import _ from '../locale';
import {
    AI_CHAT_MESSAGE_KIND,
    isAiChatAssistantMessage,
    isAiChatSystemMessage,
    rendersInlineResult,
} from '../models/ai-chat-message';
import { AGE_GROUP_ID_KEY } from '../models/data-key';
import { sortDataTypes } from '../models/data-type';
import {
    AgeGroupsOrder,
    AiChatDataCollectionOption,
    AiChatDataTypes,
    AiChatFeedbackDetails,
    AiChatFilterSet,
    AiChatHistoryMessage,
    AiChatResolvedDataCollection,
    ChartType,
    DataFilters,
    DataGranularityKey,
    DataQueryParams,
    DataQueryType,
    DataType,
    DataValue,
} from '../types';
import { formatValue } from './filters-helpers';

export const hasFeedbackDetails = (feedback: AiChatFeedbackDetails): boolean =>
    !isEmpty(feedback.comment) || !isEmpty(feedback.categories);

type RawAssistantFilterValue = DataValue | string | number | null | undefined;

export const parseFilterValue = (rawValue: RawAssistantFilterValue): DataValue => {
    if (Array.isArray(rawValue)) {
        return rawValue;
    }

    if (rawValue == null || rawValue === '') {
        return [];
    }

    const values = String(rawValue)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    const numericValues = values.map(Number);

    // Only coerce to numbers when it round-trips exactly, so zero-padded codes ('06075')
    // and out-of-safe-range values stay strings rather than becoming corrupted ids.
    const isLosslessNumeric = values.every(
        (value, idx) => Number.isFinite(numericValues[idx]) && String(numericValues[idx]) === value
    );

    return isLosslessNumeric ? numericValues : values;
};

// Temporary compatibility layer. Remove once the backend sends filters in the
// correct DataFilters format.
export const parseAssistantFilters = (filters: DataFilters): DataFilters =>
    Object.entries(filters).reduce<DataFilters>((acc, [key, value]) => {
        const parsedValue = parseFilterValue(value);

        if (!isEmpty(parsedValue)) {
            acc[key as DataGranularityKey] = parsedValue;
        }

        return acc;
    }, {});

const DATA_TYPES_SEPARATOR = ',';

export const parseDataTypes = (value: AiChatDataTypes | null | undefined): DataType[] =>
    sortDataTypes(
        compact(
            (value ?? '').split(DATA_TYPES_SEPARATOR).map((dataType) => dataType.trim())
        ) as DataType[]
    );

export const serializeDataTypes = (dataTypes: DataType[]): AiChatDataTypes =>
    sortDataTypes(dataTypes).join(DATA_TYPES_SEPARATOR) as AiChatDataTypes;

export type ParsedAssistantContent =
    | ({
          kind: typeof AI_CHAT_MESSAGE_KIND.FILTERS;
          resolvedDataCollections?: AiChatResolvedDataCollection[];
      } & AiChatFilterSet)
    | ({
          kind: typeof AI_CHAT_MESSAGE_KIND.QUERY_RESULT;
          resolvedDataCollections?: AiChatResolvedDataCollection[];
      } & ParsedQueryResult)
    | { kind: typeof AI_CHAT_MESSAGE_KIND.TEXT; content: string }
    | null;

// Only forward non-empty params; the API sends empty strings for unused ones.
export const pickNonEmptyParams = (params: DataQueryParams | undefined): DataQueryParams =>
    Object.fromEntries(
        Object.entries(params ?? {}).filter(([, value]) => !isEmpty(value))
    ) as DataQueryParams;

export interface AiChatSuccessPayload {
    query_type?: DataQueryType | string;
    data_type?: AiChatDataTypes;
    filters?: DataFilters;
    params?: DataQueryParams;
    visualization?: ChartType;
    resolved_data_collections?: AiChatResolvedDataCollection[];
}

export interface ParsedQueryResult extends AiChatFilterSet {
    queryType: DataQueryType;
    params: DataQueryParams;
}

// The filters to render: server-applied when present, else the AI's proposed ones.
export const resolveAssistantFilters = (
    success: AiChatSuccessPayload,
    appliedFilters?: DataFilters | null
): DataFilters => parseAssistantFilters(appliedFilters ?? success.filters ?? {});

export const resolveAssistantDataTypes = (
    success: AiChatSuccessPayload,
    appliedDataType?: AiChatDataTypes | null
): DataType[] => parseDataTypes(appliedDataType ?? success.data_type);

export const toDataCollectionOptions = (
    dataCollections: AiChatResolvedDataCollection[],
    answerDataTypes: DataType[]
): AiChatDataCollectionOption[] =>
    [...dataCollections]
        .sort((a, b) => (b.data_percentage ?? 0) - (a.data_percentage ?? 0))
        .map(({ id, name, data_percentage: dataPercentage, data_type: dataType, filters }) => ({
            id,
            name,
            dataPercentage: dataPercentage ?? null,
            dataTypes: dataType ? parseDataTypes(dataType) : answerDataTypes,
            filters: parseAssistantFilters(filters ?? {}),
        }));

// The option with the highest data percentage; none when no option reports one.
export const getBestMatchDataCollectionId = (
    options: AiChatDataCollectionOption[]
): number | null =>
    maxBy(
        'dataPercentage',
        options.filter(({ dataPercentage }) => dataPercentage != null)
    )?.id ?? null;

const getHistoryAnswerError = (messages: AiChatHistoryMessage[], index: number): string | null => {
    const next = messages[index + 1];

    return next && isAiChatSystemMessage(next) && !isEmpty(next.content) ? next.content : null;
};

export interface AiChatHistoryDataCollectionPicker {
    content: string | null;
    options: AiChatDataCollectionOption[];
    pickedDataCollectionId: number | null;
    isDismissed: boolean;
}

const getHistoryDataCollectionPicker = (
    message: AiChatHistoryMessage,
    options: AiChatDataCollectionOption[],
    answerError: string | null,
    isLastAnswer: boolean
): AiChatHistoryDataCollectionPicker | null => {
    const dataCollectionId = message.data_collection_id ?? null;

    if (options.length < (answerError ? 1 : 2)) {
        return null;
    }

    if (dataCollectionId == null) {
        return {
            content: answerError,
            options,
            pickedDataCollectionId: null,
            isDismissed: !isLastAnswer,
        };
    }

    return options.some(({ id }) => id === dataCollectionId)
        ? {
              content: answerError,
              options,
              pickedDataCollectionId: dataCollectionId,
              isDismissed: false,
          }
        : null;
};

export const parseQueryResultPayload = (
    success: AiChatSuccessPayload | null | undefined,
    appliedFilters?: DataFilters | null,
    appliedDataType?: AiChatDataTypes | null
): ParsedQueryResult | null => {
    const { data_type: dataType, query_type: queryType, params, visualization } = success ?? {};
    if (!success || !dataType || !rendersInlineResult(queryType)) {
        return null;
    }

    return {
        queryType,
        dataTypes: resolveAssistantDataTypes(success, appliedDataType),
        filters: resolveAssistantFilters(success, appliedFilters),
        params: pickNonEmptyParams(params),
        visualization,
    };
};

const extractErrorMessage = (raw: unknown): string | null => {
    if (typeof raw === 'string') {
        return raw.trim() || null;
    }
    if (raw && typeof raw === 'object') {
        const { user_message: userMessage, system_message: systemMessage } = raw as {
            user_message?: unknown;
            system_message?: unknown;
        };
        if (typeof userMessage === 'string' && !isEmpty(userMessage.trim())) {
            return userMessage.trim();
        }
        if (typeof systemMessage === 'string' && !isEmpty(systemMessage.trim())) {
            return systemMessage.trim();
        }
    }
    return null;
};

export const parseAssistantContent = (
    message: AiChatHistoryMessage,
    answerError: string | null = null
): ParsedAssistantContent => {
    const {
        content,
        applied_filters: appliedFilters,
        applied_data_type: appliedDataType,
        resolved_data_collections: storedDataCollections,
    } = message;
    if (answerError && isEmpty(storedDataCollections)) {
        return { kind: AI_CHAT_MESSAGE_KIND.TEXT, content: answerError };
    }

    if (isEmpty(content)) {
        return null;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(content);
    } catch {
        // Older assistant payloads or clarifications are sent as plain text.
        return { kind: AI_CHAT_MESSAGE_KIND.TEXT, content };
    }

    if (typeof parsed === 'string') {
        return !isEmpty(parsed.trim())
            ? { kind: AI_CHAT_MESSAGE_KIND.TEXT, content: parsed }
            : null;
    }

    if (!parsed || typeof parsed !== 'object') {
        return { kind: AI_CHAT_MESSAGE_KIND.TEXT, content };
    }

    const { success, error } = parsed as {
        success?: AiChatSuccessPayload;
        error?: unknown;
    };

    if (success?.data_type) {
        const resolvedDataCollections = storedDataCollections ?? success.resolved_data_collections;
        const queryResult = answerError
            ? null
            : parseQueryResultPayload(success, appliedFilters, appliedDataType);
        if (queryResult) {
            return {
                kind: AI_CHAT_MESSAGE_KIND.QUERY_RESULT,
                ...queryResult,
                resolvedDataCollections,
            };
        }

        return {
            kind: AI_CHAT_MESSAGE_KIND.FILTERS,
            dataTypes: resolveAssistantDataTypes(success, appliedDataType),
            filters: resolveAssistantFilters(success, appliedFilters),
            visualization: success.visualization,
            resolvedDataCollections,
        };
    }

    const errorMessage = extractErrorMessage(error);
    if (errorMessage) {
        return { kind: AI_CHAT_MESSAGE_KIND.TEXT, content: errorMessage };
    }

    // Unknown shape surface the raw content so the conversation isn't silently dropped.
    return { kind: AI_CHAT_MESSAGE_KIND.TEXT, content };
};

export interface ParsedHistoryAnswer {
    parsed: NonNullable<ParsedAssistantContent>;
    dataCollectionOptions: AiChatDataCollectionOption[];
    picker: AiChatHistoryDataCollectionPicker | null;
}

export const parseHistoryAnswer = (
    messages: AiChatHistoryMessage[],
    index: number
): ParsedHistoryAnswer | null => {
    const message = messages[index];
    if (!isAiChatAssistantMessage(message)) {
        return null;
    }

    const answerError = getHistoryAnswerError(messages, index);
    const parsed = parseAssistantContent(message, answerError);
    if (!parsed) {
        return null;
    }

    if (parsed.kind === AI_CHAT_MESSAGE_KIND.TEXT) {
        return { parsed, dataCollectionOptions: [], picker: null };
    }

    const dataCollectionOptions = toDataCollectionOptions(
        parsed.resolvedDataCollections ?? [],
        parsed.dataTypes
    );
    const isLastAnswer = messages.slice(index + 1).every(isAiChatSystemMessage);

    return {
        parsed,
        dataCollectionOptions,
        picker: getHistoryDataCollectionPicker(
            message,
            dataCollectionOptions,
            answerError,
            isLastAnswer
        ),
    };
};

export const parseManualFiltersContent = (
    message: AiChatHistoryMessage
): AiChatFilterSet | null => {
    let payload: AiChatSuccessPayload | null;
    try {
        payload = JSON.parse(message.content);
    } catch {
        return null;
    }

    if (!payload?.data_type) {
        return null;
    }

    return {
        dataTypes: resolveAssistantDataTypes(payload, message.applied_data_type),
        filters: resolveAssistantFilters(payload, message.applied_filters),
        visualization: payload.visualization,
    };
};

export interface LocalizedFilter {
    key: string;
    label: string;
    firstValue: string;
    remainingValues: string[];
}

export const localizeFilters = (
    filters: DataFilters,
    dataType: DataType,
    ageGroupsOrder?: AgeGroupsOrder | null
): LocalizedFilter[] =>
    Object.entries(filters)
        .map(([key, rawValues]): LocalizedFilter | null => {
            if (isEmpty(rawValues)) {
                return null;
            }

            const orderedRawValues =
                key === AGE_GROUP_ID_KEY && ageGroupsOrder
                    ? [...(rawValues as DataValue)].sort(
                          (a, b) => ageGroupsOrder[a] - ageGroupsOrder[b]
                      )
                    : (rawValues as DataValue);

            const localizedValues = orderedRawValues.map((value) =>
                String(formatValue(key, value, _, undefined, dataType))
            );

            return {
                key,
                label: _(`refinement_${key}`),
                firstValue: localizedValues[0],
                remainingValues: localizedValues.slice(1),
            };
        })
        .filter((entry): entry is LocalizedFilter => entry !== null);
